/**
 * Handles client-side errors from OfficeRuntime.auth.getAccessToken.
 *
 * Error code mappings and platform-specific behaviors:
 *
 * Office Web (Outlook on the web):
 * - 13001: User not signed into Office - fallback recommended
 * - 13002: User denied consent - don't fallback automatically (let user retry manually)
 * - 13006: Problem in Office on the Web - fallback recommended
 * - 13008: Office is busy - fallback recommended
 * - 13010: Browser zone configuration issue - fallback recommended
 *
 * Office Desktop (Windows/Mac):
 * - Generally more reliable SSO, but same error codes apply
 * - Fallback to dialog is safer for all non-consent errors
 *
 * @param error The error object from the catch block.
 * @returns boolean indicating whether to invoke the fallback dialog.
 */
export function handleClientSideErrors(error: any): boolean {
  const message = (text: string) => {
    console.warn("Office SSO Error:", text);
    // In a real app, you'd want to update the UI state with this message.
  };

  switch (error.code) {
    case 13001: // No one is signed into Office
      message("No one is signed into Office. Please sign in to use this feature.");
      // We should allow fallback so the user can sign in.
      return true;

    case 13002: // User aborted the consent prompt
      message("You did not grant consent. If you want to proceed, please try again.");
      // Don't fall back immediately; let the user click the button again if they change their mind.
      // This prevents aggressive fallback when user intentionally cancels.
      return false;

    // Platform-specific errors that commonly occur in Office Web
    case 13006: // Problem in Office on the Web
      message("Single Sign-On is unavailable due to a client issue. Let's try an alternative sign-in method.");
      return true; // Fallback to dialog

    case 13008: // Office is busy
      message("Office is currently busy. Retrying with alternative authentication.");
      return true; // Fallback to dialog

    case 13010: // Browser zone configuration issue (common in Office Web)
      message("Browser configuration issue detected. Using alternative sign-in method.");
      return true; // Fallback to dialog

    default:
      // For all other errors (13000, 13003, 13005, 13007, 13012, 50001),
      // falling back to the dialog is the safest option.
      // These may include network issues, server errors, or unknown conditions.
      message(`Unhandled Office error (${error.code}). Falling back to dialog authentication.`);
      return true;
  }
}
